import { z } from 'zod';
import { RequestRefusalReasonSchema } from '../requests/status.js';
import { RequestStatus } from '../requests/status.js';

// China-requests leadership dashboard wire contract (#1588, Epic #1586).
// Single source of truth for `GET /api/dashboards/china-requests/summary`;
// the backend infers its DTOs from these schemas and never redeclares them.
//
// CHINA REQUESTS = the `Request` model. All windows are half-open
// `[from, to)` MSK boundaries projected to UTC instants; snapshot KPIs
// (`inWorkNow`, `overdueNow`, `hotNow`) read the request instant `now`,
// not the window.

/**
 * @aiPurpose period preset selector for the summary endpoint. `custom`
 *   requires `from`/`to`; every preset resolves server-side against the
 *   Europe/Moscow business clock (UTC+3, no DST).
 */
export const ChinaRequestsPeriodKeySchema = z.enum([
  'today',
  'last7d',
  'month',
  'quarter',
  'custom',
]);
export type ChinaRequestsPeriodKey = z.infer<typeof ChinaRequestsPeriodKeySchema>;

/**
 * @aiPurpose query contract for `GET /api/dashboards/china-requests/summary`.
 *   `from`/`to` are ISO-8601 date-times required ONLY when `period=custom`
 *   (inclusive `from`, EXCLUSIVE `to`). The cross-field rule rejects a
 *   custom window with missing bounds or `from >= to` so the backend's
 *   ZodValidationPipe returns a typed 400 before the service runs.
 * @aiInvariant non-custom presets ignore any supplied `from`/`to` — the
 *   server derives the window from the MSK business clock. `.strict()`
 *   rejects stray keys so a typo fails loudly instead of silently widening.
 */
export const ChinaRequestsSummaryQuerySchema = z
  .object({
    period: ChinaRequestsPeriodKeySchema.default('month'),
    from: z.string().datetime({ offset: true }).optional(),
    to: z.string().datetime({ offset: true }).optional(),
  })
  .strict()
  .superRefine((value, ctx) => {
    if (value.period !== 'custom') return;
    if (value.from === undefined || value.to === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'custom period requires both from and to',
        path: [value.from === undefined ? 'from' : 'to'],
      });
      return;
    }
    if (Date.parse(value.from) >= Date.parse(value.to)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'custom period requires from < to',
        path: ['to'],
      });
    }
  });
export type ChinaRequestsSummaryQuery = z.infer<typeof ChinaRequestsSummaryQuerySchema>;

// One effective weight (kg) + volume (m³) pair. Numbers (not money
// strings): the underlying `Decimal(13,4)` columns survive a JS number
// well within the registry's magnitude, mirroring the request wire's
// `weight`/`volume` projection.
const WeightVolumeSchema = z
  .object({
    weightKg: z.number(),
    volumeM3: z.number(),
  })
  .strict();

const CountKpiSchema = z
  .object({
    value: z.number().int().nonnegative(),
    previous: z.number().int().nonnegative(),
    // null when the previous window's value is 0 (no division base).
    deltaPct: z.number().nullable(),
  })
  .strict();

const SnapshotKpiSchema = z
  .object({
    value: z.number().int().nonnegative(),
  })
  .strict();

const ConversionKpiSchema = z
  .object({
    // null when the closed-cohort denominator (SUCCESS + REFUSED) is 0.
    value: z.number().nullable(),
    previous: z.number().nullable(),
    // percentage-point delta; null when either side is null.
    deltaPp: z.number().nullable(),
  })
  .strict();

const VolumeKpiSchema = z
  .object({
    total: WeightVolumeSchema,
    // CONTAINER-kind contribution.
    container: WeightVolumeSchema,
    // STANDARD + SIMPLE contribution; total === container + nonContainer.
    nonContainer: WeightVolumeSchema,
    previousTotal: WeightVolumeSchema,
  })
  .strict();

const ChinaRequestsKpisSchema = z
  .object({
    newRequests: CountKpiSchema,
    inWorkNow: SnapshotKpiSchema,
    success: CountKpiSchema,
    conversionPct: ConversionKpiSchema,
    overdueNow: SnapshotKpiSchema,
    volume: VolumeKpiSchema,
  })
  .strict();

const FunnelEntrySchema = z
  .object({
    status: z.nativeEnum(RequestStatus),
    count: z.number().int().nonnegative(),
  })
  .strict();

const SlaSchema = z
  .object({
    overdueNow: z.number().int().nonnegative(),
    hotNow: z.number().int().nonnegative(),
    // null when no row in the period has both inWorkAt and processedAt set.
    avgProcessingDays: z.number().nullable(),
    // count of rows whose LATEST reopenedAt falls in the period.
    reopenCount: z.number().int().nonnegative(),
  })
  .strict();

const ManagerBreakdownSchema = z
  .object({
    managerId: z.string().uuid(),
    managerName: z.string(),
    created: z.number().int().nonnegative(),
    inWorkNow: z.number().int().nonnegative(),
    success: z.number().int().nonnegative(),
    refused: z.number().int().nonnegative(),
    conversionPct: z.number().nullable(),
    weightKg: z.number(),
    volumeM3: z.number(),
  })
  .strict();

const RefusalReasonEntrySchema = z
  .object({
    reason: RequestRefusalReasonSchema,
    count: z.number().int().nonnegative(),
  })
  .strict();

/**
 * @aiPurpose response body for `GET /api/dashboards/china-requests/summary`.
 *   Company-wide on-read aggregation over the `Request` model: KPIs, status
 *   funnel, SLA stats, per-manager breakdown, and refusal-reason counts for
 *   the selected period, with a comparison to the immediately-preceding
 *   equal-length window.
 * @aiInvariant ARCHIVE is excluded from EVERY metric; the active/SLA set is
 *   `{READY_FOR_WORK, IN_WORK}`. `period.from`/`to` and `previous.from`/`to`
 *   are ISO-8601 UTC instants; `period.label` is a human MSK label.
 * @aiPii none — aggregates plus manager display names only. No per-request
 *   row data, no refusal comments.
 */
/**
 * @aiPurpose time-bucket granularity for the China-requests trend endpoint
 *   (#1590). The server picks one bucket from the resolved window span — the
 *   client never sends it; it reads it back to format the x-axis.
 */
export const ChinaRequestsTrendBucketSchema = z.enum(['day', 'week', 'month']);
export type ChinaRequestsTrendBucket = z.infer<typeof ChinaRequestsTrendBucketSchema>;

// One time-bucket point. `bucketStart` is the UTC instant of the bucket's
// MSK start edge (day 00:00 / ISO-week Monday 00:00 / month-1st 00:00). The
// first bucket may start at or before the window `from` (partial first bucket);
// counts are clipped to the half-open `[from, to)` window by the SQL bound.
const ChinaRequestsTrendPointSchema = z
  .object({
    bucketStart: z.string().datetime(),
    // created cohort by `createdAt` MSK bucket (status <> ARCHIVE).
    created: z.number().int().nonnegative(),
    // closed cohort by `closedAt` MSK bucket (SUCCESS/REFUSED/CLOSED).
    closed: z.number().int().nonnegative(),
    // SUCCESS subset of the closed cohort by `closedAt` MSK bucket.
    success: z.number().int().nonnegative(),
  })
  .strict();

/**
 * @aiPurpose response body for `GET /api/dashboards/china-requests/trend`.
 *   A continuous created-vs-closed(+success) series over the selected period,
 *   one point per MSK time bucket tiling `[from, to)` left→right. Empty
 *   buckets are emitted as zeros so the x-axis is continuous. The query reuses
 *   `ChinaRequestsSummaryQuerySchema` (same params/validation).
 * @aiInvariant `points` are ordered ascending by `bucketStart`; ARCHIVE is
 *   excluded from every series. `bucket` is server-chosen from the window span.
 * @aiPii none — bucketed counts only, no per-request data.
 */
export const ChinaRequestsDashboardTrendSchema = z
  .object({
    bucket: ChinaRequestsTrendBucketSchema,
    points: z.array(ChinaRequestsTrendPointSchema),
  })
  .strict();
export type ChinaRequestsDashboardTrend = z.infer<typeof ChinaRequestsDashboardTrendSchema>;

export const ChinaRequestsDashboardSummarySchema = z
  .object({
    period: z
      .object({
        key: z.string(),
        from: z.string().datetime(),
        to: z.string().datetime(),
        label: z.string(),
      })
      .strict(),
    previous: z
      .object({
        from: z.string().datetime(),
        to: z.string().datetime(),
      })
      .strict(),
    kpis: ChinaRequestsKpisSchema,
    funnel: z.array(FunnelEntrySchema),
    sla: SlaSchema,
    managers: z.array(ManagerBreakdownSchema),
    refusalReasons: z.array(RefusalReasonEntrySchema),
    computedAt: z.string().datetime(),
  })
  .strict();
export type ChinaRequestsDashboardSummary = z.infer<typeof ChinaRequestsDashboardSummarySchema>;
